/**
 * Viral Wave Detector — predicts virality 48h out.
 *
 * The "sci-fi" moment of the SociBaba demo. Shows a live feed of viral
 * signals detected across X / Reddit / YouTube / Instagram / Threads,
 * with velocity + sentiment + your-brand exposure + AI-recommended action.
 *
 * Block 5 build: reads viral_signals seeded in Block 2 + adds a "trajectory"
 * mini-chart per signal + an "Auto-Draft Response" streaming button.
 */
import React from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Waves, Zap, TrendingUp, AlertTriangle, ArrowUpRight, Sparkles, BellRing, Radar } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ViralSignal {
  signal_id: string;
  topic: string;
  slug?: string;
  sentiment: "positive" | "negative" | "mixed";
  velocity_per_hour: number;
  predicted_peak_hours: number;
  reach_estimate: number;
  your_exposure: "low" | "medium" | "high";
  sources: string[];
  recommended_action: string;
  detected_at: string;
}

function trajectory(seed: number, peak: number): number[] {
  const pts: number[] = [];
  const rng = (i: number) => Math.abs(Math.sin(seed * 13.37 + i));
  for (let i = 0; i < 24; i++) {
    const t = i / 23;
    const bell = Math.exp(-Math.pow((t - 0.62), 2) * 12);
    pts.push(Math.round(bell * peak * (0.72 + rng(i) * 0.6)));
  }
  return pts;
}

function Sparkline({ data, tone }: { data: number[]; tone: string }) {
  const max = Math.max(1, ...data);
  const w = 240;
  const h = 60;
  const step = w / (data.length - 1);
  const path = data
    .map((v, i) => `${i === 0 ? "M" : "L"} ${(i * step).toFixed(1)} ${(h - (v / max) * (h - 6) - 2).toFixed(1)}`)
    .join(" ");
  const area = `${path} L ${w} ${h} L 0 ${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-14" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`grad-${tone}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={tone} stopOpacity="0.4"/>
          <stop offset="100%" stopColor={tone} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#grad-${tone})`} />
      <path d={path} fill="none" stroke={tone} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

const EXPOSURE_TONE: Record<string, string> = {
  low: "bg-blue-50 text-blue-700 border-blue-200",
  medium: "bg-amber-50 text-amber-800 border-amber-200",
  high: "bg-rose-50 text-rose-700 border-rose-200",
};

const SENTIMENT_TONE: Record<string, { bg: string; label: string; color: string }> = {
  positive: { bg: "from-emerald-500/20 to-emerald-500/0",  color: "#10b981", label: "Positive" },
  negative: { bg: "from-rose-500/20 to-rose-500/0",        color: "#f43f5e", label: "Negative" },
  mixed:    { bg: "from-amber-500/20 to-amber-500/0",      color: "#f59e0b", label: "Mixed" },
};

export default function ViralWaves() {
  const q = useQuery({
    queryKey: ["viral-signals"],
    queryFn: async () => {
      try {
        const r = await api.get("/viral-signals");
        return r.data as ViralSignal[];
      } catch {
        // Fallback — the /viral-signals endpoint isn't ready yet, but the seed
        // populated the collection. We hit a mirrored path.
        const r = await api.get("/discovery/trends");
        return (r.data?.items || []) as any[];
      }
    },
  });

  // Fallback to a small hardcoded set when API isn't wired yet.
  const signals: ViralSignal[] = (Array.isArray(q.data) && q.data.length ? q.data : [
    { signal_id: "s1", topic: "iPhone 17 battery drain", sentiment: "negative", velocity_per_hour: 8400, predicted_peak_hours: 12, reach_estimate: 2_400_000, your_exposure: "high", sources: ["x","reddit","youtube"], recommended_action: "Draft public apology + fix comms in 2h before wave peaks", detected_at: new Date().toISOString() },
    { signal_id: "s2", topic: "Diwali sale mania", sentiment: "positive", velocity_per_hour: 14200, predicted_peak_hours: 24, reach_estimate: 6_100_000, your_exposure: "medium", sources: ["instagram","facebook","threads"], recommended_action: "Boost hero SKUs by 30% now — ride the wave", detected_at: new Date().toISOString() },
    { signal_id: "s3", topic: "EV subsidy news breaking", sentiment: "positive", velocity_per_hour: 3900, predicted_peak_hours: 8, reach_estimate: 1_200_000, your_exposure: "low", sources: ["x","reddit"], recommended_action: "Publish contextual blog + reel", detected_at: new Date().toISOString() },
    { signal_id: "s4", topic: "Amazon Great Sale glitch", sentiment: "mixed", velocity_per_hour: 5600, predicted_peak_hours: 6, reach_estimate: 890_000, your_exposure: "medium", sources: ["reddit","x","facebook"], recommended_action: "Watch closely — pivot only if narrative shifts negative", detected_at: new Date().toISOString() },
    { signal_id: "s5", topic: "Streamer collab going viral", sentiment: "positive", velocity_per_hour: 11200, predicted_peak_hours: 18, reach_estimate: 3_400_000, your_exposure: "high", sources: ["youtube","instagram","x"], recommended_action: "Reach out to creator — collab window is now", detected_at: new Date().toISOString() },
    { signal_id: "s6", topic: "Air India delay chaos", sentiment: "negative", velocity_per_hour: 6800, predicted_peak_hours: 10, reach_estimate: 1_700_000, your_exposure: "low", sources: ["x","threads"], recommended_action: "No brand exposure — pause travel-adjacent ads for 24h", detected_at: new Date().toISOString() },
  ]) as ViralSignal[];

  const [drafting, setDrafting] = React.useState<string | null>(null);
  const [drafted, setDrafted] = React.useState<Record<string, string>>({});

  const draftResponse = (s: ViralSignal) => {
    setDrafting(s.signal_id);
    const target =
      s.sentiment === "negative"
        ? `We hear you on "${s.topic}". Our team is actively investigating and will share a firm update within 4 hours. Meanwhile, DM us your order ID and we'll fast-track any concern personally. — Team SociBaba`
        : s.sentiment === "positive"
        ? `We love that "${s.topic}" is resonating! ✨ We've unlocked a 24-hour community boost — first 100 orders get an extra 15% off + priority shipping. Link in bio.`
        : `Thanks for weighing in on "${s.topic}". We're watching closely — here's the full transparent breakdown of what we know so far and what we're doing about it: (link in bio).`;
    let i = 0;
    const id = window.setInterval(() => {
      i += 6;
      setDrafted((d) => ({ ...d, [s.signal_id]: target.slice(0, i) }));
      if (i >= target.length) {
        window.clearInterval(id);
        setDrafting(null);
      }
    }, 60);
  };

  return (
    <DashboardLayout
      title="Viral Wave Detector"
      subtitle="See the wave 48 hours before it peaks — and know exactly what to do."
      seoNoindex
    >
      {/* Vision strip */}
      <div className="rounded-2xl overflow-hidden border border-[color:var(--sg-border)] relative mb-6" data-testid="viral-vision">
        <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500/10 via-transparent to-blue-500/10" />
        <div className="relative px-6 py-6 sm:px-8 sm:py-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-fuchsia-500 to-blue-500 flex items-center justify-center shrink-0 shadow-lg shadow-fuchsia-500/25">
              <Waves className="h-5 w-5 text-white" strokeWidth={2.5}/>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--sg-muted)]">Predictive intelligence</div>
              <div className="font-serif-display text-xl sm:text-2xl font-bold text-[color:var(--sg-fg)] leading-tight">
                {signals.filter(s => s.your_exposure === "high").length} high-exposure waves detected. Peak in {Math.min(...signals.map(s => s.predicted_peak_hours))}h.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300 bg-emerald-100/60 dark:bg-emerald-900/30 px-2.5 py-1 rounded-full">
              <Radar className="h-3 w-3" strokeWidth={2.5}/> Scanning 6 platforms · 4.2M signals/hr
            </div>
          </div>
        </div>
      </div>

      {/* Signal grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {signals.map((s, idx) => {
          const st = SENTIMENT_TONE[s.sentiment];
          const traj = trajectory(idx + 1, s.velocity_per_hour);
          return (
            <div
              key={s.signal_id}
              className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-5 relative overflow-hidden group hover:border-fuchsia-400/40 transition-colors"
              data-testid={`viral-signal-${s.signal_id}`}
            >
              <div className={`absolute -top-16 -right-16 h-40 w-40 rounded-full bg-gradient-to-br ${st.bg} blur-3xl group-hover:opacity-80 transition-opacity`} />
              <div className="relative">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] font-semibold text-[color:var(--sg-muted)]">
                      <BellRing className="h-3 w-3" strokeWidth={2.5}/> Detected {new Date(s.detected_at).toLocaleTimeString([], {hour:"2-digit", minute:"2-digit"})}
                    </div>
                    <div className="mt-1 font-serif-display text-xl font-bold text-[color:var(--sg-fg)] leading-tight">
                      {s.topic}
                    </div>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-full border whitespace-nowrap uppercase tracking-wider ${EXPOSURE_TONE[s.your_exposure]}`}>
                    {s.your_exposure} exposure
                  </span>
                </div>

                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-[color:var(--sg-muted-bg)] px-2 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-[color:var(--sg-muted)]">Velocity</div>
                    <div className="text-sm font-bold text-[color:var(--sg-fg)] tabular-nums">{s.velocity_per_hour.toLocaleString()}/hr</div>
                  </div>
                  <div className="rounded-lg bg-[color:var(--sg-muted-bg)] px-2 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-[color:var(--sg-muted)]">Peak in</div>
                    <div className="text-sm font-bold text-[color:var(--sg-fg)] tabular-nums">{s.predicted_peak_hours}h</div>
                  </div>
                  <div className="rounded-lg bg-[color:var(--sg-muted-bg)] px-2 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-[color:var(--sg-muted)]">Reach</div>
                    <div className="text-sm font-bold text-[color:var(--sg-fg)] tabular-nums">{(s.reach_estimate/1_000_000).toFixed(1)}M</div>
                  </div>
                </div>

                <div className="mt-3">
                  <Sparkline data={traj} tone={st.color}/>
                </div>

                <div className="mt-2 flex items-center gap-2 text-[11px] text-[color:var(--sg-muted)] flex-wrap">
                  <span>Sentiment · <b className="text-[color:var(--sg-fg)]">{st.label}</b></span>
                  <span>·</span>
                  <span>Sources: {(s.sources || []).slice(0, 3).join(", ")}</span>
                </div>

                <div className="mt-3 rounded-lg border border-dashed border-[color:var(--sg-border)] bg-[color:var(--sb-cream,#F4F1EA)]/50 dark:bg-white/5 p-3">
                  <div className="flex items-start gap-2">
                    <Sparkles className="h-4 w-4 text-[color:var(--sb-saffron,#F97316)] shrink-0 mt-0.5" strokeWidth={2.5}/>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[color:var(--sg-muted)]">AI Recommendation</div>
                      <div className="text-sm text-[color:var(--sg-fg)] mt-0.5">{s.recommended_action}</div>
                    </div>
                  </div>
                </div>

                {drafted[s.signal_id] && (
                  <div className="mt-3 rounded-lg bg-[color:var(--sg-card)] border border-[color:var(--sg-border)] p-3" data-testid={`viral-draft-${s.signal_id}`}>
                    <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[color:var(--sg-muted)] mb-1 flex items-center gap-1">
                      <Zap className="h-3 w-3 text-[color:var(--sb-saffron,#F97316)]"/> Draft response · brand voice
                    </div>
                    <div className="text-sm text-[color:var(--sg-fg)] whitespace-pre-wrap">
                      {drafted[s.signal_id]}{drafting === s.signal_id && <span className="inline-block h-4 w-2 bg-[color:var(--sb-saffron,#F97316)] ml-0.5 animate-pulse"/>}
                    </div>
                  </div>
                )}

                <div className="mt-3 flex items-center gap-2">
                  <Button
                    data-testid={`viral-draft-btn-${s.signal_id}`}
                    onClick={() => draftResponse(s)}
                    disabled={drafting === s.signal_id}
                    className="bg-gradient-to-r from-[color:var(--sb-saffron,#F97316)] to-fuchsia-500 text-white hover:opacity-90 h-9 text-xs font-semibold"
                  >
                    <Sparkles className="h-3.5 w-3.5 mr-1"/> Auto-Draft Response
                  </Button>
                  <Button variant="outline" className="h-9 text-xs" data-testid={`viral-boost-btn-${s.signal_id}`}>
                    <TrendingUp className="h-3.5 w-3.5 mr-1"/> Boost content
                  </Button>
                  <Button variant="ghost" className="h-9 text-xs text-[color:var(--sg-muted)]">
                    <ArrowUpRight className="h-3.5 w-3.5 mr-1"/> Inspect
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </DashboardLayout>
  );
}
